/**
 * Tabela de marcos superficiais do Resumo Técnico — GUPM-329.
 *
 * O marco não cabe na tabela principal, que tem UMA coluna de leitura por linha: a
 * campanha topográfica traz três grandezas. Desenho confirmado pelo dono em
 * 10/09/2026 — seção própria, exibindo o deslocamento (ΔLeste, ΔNorte, Recalque) e
 * não as coordenadas cruas; e completado em 11/09/2026 com a **Cota**.
 *
 * Por que não as coordenadas: para o marco a posição É a medição, mas o par
 * E/N absoluto (296.462 m / 7.966.762 m) não informa nada a quem acompanha a
 * barragem. O que importa é o quanto ele andou desde a implantação — milímetros.
 *
 * A **cota** é a exceção, e por isso ficou: diferente de E/N, a altitude absoluta é
 * lida diretamente (803,280 m contra a crista). Recalque e Cota respondem perguntas
 * distintas — *quanto afundou* e *onde está* — e a tabela mostra as duas.
 *
 * Sem colunas de limiar, ao contrário da tabela de tiltímetros: a planilha da Galvani
 * não traz Carta de Controle para marco, e o card decidiu **não inventar limiar**
 * (ADR-0027 — o instrumento exibe "sem faixa"). Quando a Carta chegar, as colunas de
 * Atenção/Alerta/Emergência entram aqui no mesmo formato do tiltímetro.
 */
import type { Instrument } from '@/domain';
import type { LinhaMarco } from '@/domain/instrumentos/linhas-de-marco';

export interface TabelaMarcosProps {
  linhas: LinhaMarco[];
  /** Retirados viram nota de rodapé, não linha (convenção da tabela principal). */
  retirados: Instrument[];
  /** Modo mapa: clicar no código destaca o pino. */
  onSelect?: (id: string) => void;
  selectedId?: string | null;
}

/**
 * Número em pt-BR com uma casa; `null` vira travessão.
 *
 * Travessão e não zero: zero afirmaria "medimos e não houve movimento". O `MS-2` da
 * Mina existe em campo e nunca foi medido — mesma distinção que o GUPM-228 fixou
 * para leitura ausente.
 */
const fmt = (n: number | null): string =>
  n == null ? '—' : n.toFixed(1).replace('.', ',');

/**
 * Cota em metros com três casas — o milímetro da altitude, que é a resolução que a
 * campanha topográfica entrega (803,280). Uma casa só, como no deslocamento,
 * apagaria justamente a variação que o recalque mede.
 */
const fmtCota = (n: number | null): string =>
  n == null ? '—' : n.toFixed(3).replace('.', ',');

/** Data curta da campanha; `null` vira travessão. */
const fmtData = (d: Date | null): string =>
  d == null ? '—' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });

export default function TabelaMarcos({
  linhas, retirados, onSelect, selectedId = null,
}: TabelaMarcosProps) {
  if (linhas.length === 0 && retirados.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-jf-muted">
          Marcos Superficiais (Leitura Manual)
        </h3>
        {/* Unidade e referência no cabeçalho, não repetidas em cada célula. O "desde a
            implantação" é o que impede ler ΔLeste como variação desde a campanha
            anterior — são coisas diferentes, e a planilha usa a implantação. */}
        <span className="text-[10px] text-jf-dim">deslocamento em mm, desde a implantação</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-jf-border text-left text-jf-dim">
              <th className="pb-2 pr-3 font-semibold">Instrumento</th>
              {/* ΔLeste/ΔNorte em vez de "E"/"N": a coluna mostra deslocamento, não
                  coordenada, e o rótulo tem de dizer isso. */}
              <th className="pb-2 pr-3 text-right font-semibold">ΔLeste</th>
              <th className="pb-2 pr-3 text-right font-semibold">ΔNorte</th>
              {/* Recalque é o termo da engenharia para o vertical, e carrega o sinal da
                  planilha operacional da Galvani: negativo = afundou. */}
              <th className="pb-2 pr-3 text-right font-semibold">Recalque</th>
              {/* Única coluna em METROS — a unidade vai no cabeçalho porque o resto da
                  linha está em mm, e 803,280 ao lado de 7,3 sem unidade leria como um
                  deslocamento absurdo. */}
              <th className="pb-2 pr-3 text-right font-semibold">Cota (m)</th>
              <th className="pb-2 pr-3 text-right font-semibold">Campanha</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr
                key={l.instrument.id}
                className={`border-b border-jf-border/40 ${
                  selectedId === l.instrument.id ? 'bg-jf-surface' : ''
                }`}
              >
                <td className="py-1.5 pr-3 font-bold">
                  {onSelect ? (
                    <button
                      type="button"
                      onClick={() => onSelect(String(l.instrument.id))}
                      className="text-jf-yellow hover:underline"
                    >
                      {l.instrument.code}
                    </button>
                  ) : (
                    l.instrument.code
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono font-bold">
                  {fmt(l.deslocamento.deltaLesteMm)}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono font-bold">
                  {fmt(l.deslocamento.deltaNorteMm)}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono font-bold">
                  {fmt(l.deslocamento.recalqueMm)}
                </td>
                {/* Sem `font-bold`: a cota é contexto, não o número que se acompanha —
                    o destaque fica no deslocamento, que é o que muda entre campanhas. */}
                <td className="py-1.5 pr-3 text-right font-mono text-jf-muted">
                  {fmtCota(l.cotaM)}
                </td>
                <td className="py-1.5 pr-3 text-right text-jf-dim">
                  {fmtData(l.ultimaCampanha)}
                </td>
              </tr>
            ))}
            {retirados.length > 0 && (
              <tr>
                <td colSpan={6} className="py-2 text-[10px] italic text-jf-dim">
                  {`Retirados (histórico preservado, fora do monitoramento): ${retirados
                    .map((i) => i.code)
                    .join(', ')}.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
